import type { NavigationItem, NavigationResponse } from '@/lib/navigationService';
import appShellStore from './appShellStore';

const DASHBOARD_SHELL_ENTITY_PREFIX = 'dashboard-shell';
const DASHBOARD_SHELL_VERSION = 2;

export interface DashboardShellModuleTile {
  key: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  href: string;
  sort_order: number;
  allow_mobile_app: boolean;
  user_role: string;
  permissions: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
}

export interface DashboardShellNavigationGroup {
  key: string;
  label: string;
  items: NavigationItem[];
}

export interface DashboardShellSnapshot {
  navigationData: NonNullable<NavigationResponse['data']> | null;
  mobileModules: DashboardShellModuleTile[];
  desktopNavigationGroups: DashboardShellNavigationGroup[];
  updatedAt: string;
  version: number;
}

function buildEntityId(userId: number): string {
  return `${DASHBOARD_SHELL_ENTITY_PREFIX}:${userId}`;
}

export async function saveDashboardShellSnapshot(
  clientId: number,
  userId: number,
  snapshot: Omit<DashboardShellSnapshot, 'updatedAt' | 'version'>,
): Promise<void> {
  if (!clientId || !userId) {
    return;
  }

  await appShellStore.upsertJson<DashboardShellSnapshot>(
    clientId,
    buildEntityId(userId),
    {
      ...snapshot,
      updatedAt: new Date().toISOString(),
      version: DASHBOARD_SHELL_VERSION,
    },
  );
}

export async function getDashboardShellSnapshot(
  clientId: number,
  userId: number,
): Promise<DashboardShellSnapshot | null> {
  if (!clientId || !userId) {
    return null;
  }

  const snapshot = await appShellStore.getJson<DashboardShellSnapshot>(clientId, buildEntityId(userId));
  if (!snapshot || snapshot.version !== DASHBOARD_SHELL_VERSION) {
    return null;
  }

  return snapshot;
}

export async function clearDashboardShellSnapshot(clientId: number, userId: number): Promise<void> {
  if (!clientId || !userId) {
    return;
  }

  await appShellStore.deleteJson(clientId, buildEntityId(userId));
}
